import { motion, useReducedMotion } from "framer-motion";
import type { ReactNode } from "react";

interface RevealProps {
  children: ReactNode;
  delay?: number;
  y?: number;
  className?: string;
}

/** Scroll-triggered entrance. Fires once; disabled for reduced-motion users. */
export function Reveal({ children, delay = 0, y = 24, className }: RevealProps) {
  const reduced = useReducedMotion();
  if (reduced) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

/** Masked line reveal — the signature hero entrance. */
export function MaskedLine({ children, delay = 0, show }: { children: ReactNode; delay?: number; show: boolean }) {
  const reduced = useReducedMotion();
  if (reduced) return <div className="overflow-hidden">{children}</div>;
  return (
    <div className="overflow-hidden py-[0.08em]">
      <motion.div
        initial={{ y: "115%", opacity: 0 }}
        animate={show ? { y: "0%", opacity: 1 } : { y: "115%", opacity: 0 }}
        transition={{ duration: 0.85, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    </div>
  );
}
